import * as React from "react"
import { cn } from "@/lib/utils"

export interface VCSkeletonProps extends React.HTMLAttributes<HTMLDivElement> {
  lines?: number
  avatar?: boolean
}

function SkeletonLine({ className }: { className?: string }) {
  return <div className={cn("rounded-md shimmer", className)} />
}

export function VCSkeleton({ lines, avatar, className, ...props }: VCSkeletonProps) {
  if (!lines && !avatar) {
    return <div className={cn("rounded-md shimmer", className)} {...props} />
  }

  return (
    <div className={cn("space-y-3", className)} {...props}>
      {avatar && (
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-full shimmer flex-shrink-0" />
          <div className="flex-1 space-y-2">
            <SkeletonLine className="h-4 w-3/4" />
            <SkeletonLine className="h-3 w-1/2" />
          </div>
        </div>
      )}
      {lines && Array.from({ length: lines }).map((_, i) => (
        <SkeletonLine
          key={i}
          className={cn("h-4", i === 0 ? "w-full" : i % 3 === 2 ? "w-2/3" : "w-5/6")}
        />
      ))}
    </div>
  )
}

/**
 * Skeleton for a table list page — header bar + filter row + N data rows.
 * Used as the Suspense fallback for Sales, Inventory, and Customers list pages.
 */
export function VCTableSkeleton({
  rows = 8,
  cols = 5,
  className,
}: {
  rows?: number
  cols?: number
  className?: string
}) {
  return (
    <div className={cn("space-y-4 p-4 md:p-6", className)}>
      {/* Page heading + action button */}
      <div className="flex items-center justify-between">
        <SkeletonLine className="h-8 w-48" />
        <SkeletonLine className="h-9 w-28 rounded-md" />
      </div>
      {/* Filter / search row */}
      <div className="flex gap-2 flex-wrap">
        <SkeletonLine className="h-9 w-48 rounded-md" />
        <SkeletonLine className="h-9 w-32 rounded-md" />
        <SkeletonLine className="h-9 w-32 rounded-md" />
      </div>
      {/* Table */}
      <div className="border rounded-md overflow-hidden bg-card">
        {/* Header row */}
        <div
          className="grid gap-3 px-4 py-3 border-b bg-muted/40"
          style={{ gridTemplateColumns: `repeat(${cols}, 1fr)` }}
        >
          {Array.from({ length: cols }).map((_, i) => (
            <SkeletonLine key={i} className="h-3 w-3/4" />
          ))}
        </div>
        {/* Data rows */}
        {Array.from({ length: rows }).map((_, r) => (
          <div
            key={r}
            className="grid gap-3 px-4 py-3 border-b last:border-0"
            style={{ gridTemplateColumns: `repeat(${cols}, 1fr)` }}
          >
            {Array.from({ length: cols }).map((_, c) => (
              <SkeletonLine
                key={c}
                className={cn("h-4", c === 0 ? "w-full" : c % 2 === 0 ? "w-3/4" : "w-1/2")}
              />
            ))}
          </div>
        ))}
      </div>
    </div>
  )
}

/**
 * Skeleton for a card-grid page (dashboard overview, KPI cards + charts).
 *
 * When `count` is provided the component renders that many item-card
 * skeletons instead — used for mobile list pages such as inventory.
 */
export function VCCardGridSkeleton({
  count,
  className,
}: {
  count?: number
  className?: string
}) {
  if (count != null) {
    return (
      <div className={cn("space-y-2", className)}>
        {Array.from({ length: count }).map((_, i) => (
          <div key={i} className="border rounded-lg bg-card p-4 space-y-2">
            {/* Title row */}
            <div className="flex items-start justify-between gap-2">
              <SkeletonLine className="h-4 flex-1" />
              <SkeletonLine className="h-5 w-16 rounded-full" />
            </div>
            {/* SKU */}
            <SkeletonLine className="h-3 w-24" />
            {/* Qty / meta row */}
            <div className="flex gap-4">
              <SkeletonLine className="h-3 w-16" />
              <SkeletonLine className="h-3 w-20" />
            </div>
          </div>
        ))}
      </div>
    )
  }

  return (
    <div className={cn("space-y-6 p-4 md:p-6", className)}>
      {/* Page title */}
      <SkeletonLine className="h-8 w-40" />
      {/* KPI row — 4 cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="border rounded-xl p-4 space-y-3 bg-card">
            <SkeletonLine className="h-3 w-24" />
            <SkeletonLine className="h-7 w-3/4" />
            <SkeletonLine className="h-3 w-1/2" />
          </div>
        ))}
      </div>
      {/* Chart row — 2 wide placeholders */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {Array.from({ length: 2 }).map((_, i) => (
          <div key={i} className="border rounded-xl p-4 bg-card space-y-2">
            <SkeletonLine className="h-4 w-32" />
            <SkeletonLine className="h-40 w-full rounded-md mt-2" />
          </div>
        ))}
      </div>
      {/* Secondary widgets row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="border rounded-xl p-4 bg-card space-y-2">
            <SkeletonLine className="h-4 w-24" />
            <SkeletonLine className="h-24 w-full rounded-md mt-1" />
          </div>
        ))}
      </div>
    </div>
  )
}

/**
 * Skeleton for a detail page — a page header followed by a few card sections.
 */
export function VCDetailSkeleton({ className }: { className?: string }) {
  return (
    <div className={cn("space-y-6 p-4 md:p-6", className)}>
      <div className="flex items-center gap-3">
        <SkeletonLine className="h-8 w-8 rounded-full" />
        <SkeletonLine className="h-7 w-56" />
      </div>
      {Array.from({ length: 3 }).map((_, i) => (
        <div key={i} className="border rounded-xl p-5 bg-card space-y-3">
          <SkeletonLine className="h-4 w-40" />
          <div className="grid grid-cols-2 gap-3">
            {Array.from({ length: 4 }).map((_, j) => (
              <SkeletonLine key={j} className="h-4" />
            ))}
          </div>
        </div>
      ))}
    </div>
  )
}
